import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Force-refreshing cart drawer with an "updating" state.
 * - Opens immediately for snappy UX
 * - Shows updating state while we re-render the drawer section
 * - Retries quickly to avoid race conditions (add-to-cart not finished yet)
 */
class CartDrawerComponent extends DialogComponent {
  #refreshInFlight = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('click', this.#handleInstallmentsUiClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('click', this.#handleInstallmentsUiClick);
  }

  #formatGbp(cents) {
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format((cents || 0) / 100);
    } catch {
      return `£${((cents || 0) / 100).toFixed(2)}`;
    }
  }

  #calculateMonthlyPayment(principal, annualRate, months) {
    const r = annualRate / 12;
    const numerator = principal * r * Math.pow(1 + r, months);
    const denominator = Math.pow(1 + r, months) - 1;
    return denominator === 0 ? principal / months : numerator / denominator;
  }

  #updateCartInstallmentsIllustrative(panel) {
    if (!panel) return;

    const example = panel.querySelector('[data-cart-installments-example]');
    const amountEl = panel.querySelector('[data-cart-installments-amount]');
    const resultEl = panel.querySelector('[data-cart-installments-result]');
    const activeTerm = panel.querySelector('[data-cart-installments-term].is-active') || panel.querySelector('[data-cart-installments-term]');
    if (!example || !amountEl || !resultEl || !activeTerm) return;

    const totalCents = Number(example.dataset.totalCents || 0);
    const representativeApr = Number(example.dataset.representativeApr || 22);
    const months = parseInt(activeTerm.dataset.cartInstallmentsTerm || activeTerm.getAttribute('data-cart-installments-term') || '12', 10);

    if (!Number.isFinite(totalCents) || totalCents <= 0 || !Number.isFinite(representativeApr) || representativeApr <= 0) {
      return;
    }

    const exampleAmountCents = Math.max(100000, Math.round(totalCents / 10000) * 10000);
    const principal = exampleAmountCents / 100;
    const annualRate = representativeApr / 100;
    const monthly = this.#calculateMonthlyPayment(principal, annualRate, months);
    const totalRepayable = monthly * months;
    const aprLabel = representativeApr.toFixed(2).replace(/\.00$/, '');

    amountEl.textContent = this.#formatGbp(exampleAmountCents);
    resultEl.textContent = `Approx ${this.#formatGbp(Math.round(monthly * 100))}/month over ${months} months at ${aprLabel}% Representative APR. Illustrative total repayable: ${this.#formatGbp(Math.round(totalRepayable * 100))}.`;
  }

  #handleInstallmentsUiClick = (event) => {
    const panel = this.querySelector('[data-cart-installments-panel]');
    if (!panel) return;
    const openTrigger = this.querySelector('[data-cart-installments-open]');
    const summaryLine = this.querySelector('[data-cart-installments-summary]');
    const legalLine = this.querySelector('[data-cart-installments-legal-line]');

    if (event.target.closest('[data-cart-installments-open]')) {
      event.preventDefault();
      panel.hidden = false;
      if (summaryLine) summaryLine.hidden = true;
      if (legalLine) legalLine.hidden = true;
      if (openTrigger) openTrigger.setAttribute('aria-expanded', 'true');
      this.#updateCartInstallmentsIllustrative(panel);
      return;
    }

    if (event.target.closest('[data-cart-installments-close]')) {
      event.preventDefault();
      panel.hidden = true;
      if (summaryLine) summaryLine.hidden = false;
      if (legalLine) legalLine.hidden = false;
      if (openTrigger) openTrigger.setAttribute('aria-expanded', 'false');
      return;
    }

    const termPill = event.target.closest('[data-cart-installments-term]');
    if (termPill) {
      const allPills = panel.querySelectorAll('[data-cart-installments-term]');
      allPills.forEach((pill) => {
        pill.classList.toggle('is-active', pill === termPill);
      });
      this.#updateCartInstallmentsIllustrative(panel);
    }
  };

  #handleCartAdd = async () => {
    // Open immediately for responsive UX
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    // Kick off a refresh, but don’t stack multiple refreshes
    if (!this.#refreshInFlight) {
      this.#refreshInFlight = this.#refreshSoon().finally(() => {
        this.#refreshInFlight = null;
      });
    }

    await this.#refreshInFlight;
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #getDialogEl() {
    return this.querySelector('dialog[ref="dialog"]') || this.querySelector('dialog');
  }

  #setUpdating(isUpdating) {
    const dialog = this.#getDialogEl();
    if (!dialog) return;

    dialog.classList.toggle('is-updating', !!isUpdating);
    dialog.setAttribute('aria-busy', isUpdating ? 'true' : 'false');

    // Optional: keep a simple live region for screen readers
    let live = dialog.querySelector('[data-cart-drawer-live]');
    if (!live) {
      live = document.createElement('div');
      live.setAttribute('data-cart-drawer-live', 'true');
      live.className = 'visually-hidden';
      live.setAttribute('aria-live', 'polite');
      dialog.prepend(live);
    }
    live.textContent = isUpdating ? 'Updating cart…' : '';
  }

  async #sleep(ms) {
    await new Promise((r) => setTimeout(r, ms));
  }

  async #refreshSoon() {
    // Show updating immediately (drawer is already open at this point)
    this.#setUpdating(true);

    // Attempt timings: quick/medium/longish to catch async cart update races
    const delays = [120, 260, 520];

    for (let i = 0; i < delays.length; i++) {
      if (delays[i] > 0) await this.#sleep(delays[i]);

      const ok = await this.#refreshDrawerHtml();
      if (ok) {
        this.#setUpdating(false);
        return;
      }
    }

    // If we never successfully refreshed, drop updating state anyway
    this.#setUpdating(false);
  }

  async #fetchSectionHtml(sectionId) {
    const res = await fetch(`/?section_id=${encodeURIComponent(sectionId)}`, {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!res.ok) return null;
    return await res.text();
  }

  async #fetchSectionsJson(sectionId) {
    const res = await fetch(`/?sections=${encodeURIComponent(sectionId)}`, {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!res.ok) return null;

    try {
      const json = await res.json();
      const html = json?.[sectionId];
      return typeof html === 'string' ? html : null;
    } catch {
      return null;
    }
  }

  #extractFreshDialog(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const freshComponent =
      doc.querySelector('cart-drawer-component.cart-drawer') ||
      doc.querySelector('cart-drawer-component') ||
      doc.querySelector('.cart-drawer cart-drawer-component') ||
      doc.querySelector('.cart-drawer');

    if (!freshComponent) return null;

    return (
      freshComponent.querySelector('dialog[ref="dialog"]') ||
      freshComponent.querySelector('dialog') ||
      null
    );
  }

  async #refreshDrawerHtml() {
    const currentDialog = this.#getDialogEl();
    if (!currentDialog) return false;

    const wasOpen = !!currentDialog.open;

    // Try these section handles in order:
    // 1) explicit override via data-render-section
    // 2) cart-drawer
    // 3) header (common if drawer markup lives in header section)
    const candidates = [];
    const attr = this.getAttribute('data-render-section');
    if (attr) candidates.push(attr);
    candidates.push('cart-drawer', 'header');

    for (const sectionId of candidates) {
      // 1) section_id HTML endpoint
      let html = await this.#fetchSectionHtml(sectionId);
      let freshDialog = html ? this.#extractFreshDialog(html) : null;

      // 2) fallback: sections JSON endpoint
      if (!freshDialog) {
        html = await this.#fetchSectionsJson(sectionId);
        freshDialog = html ? this.#extractFreshDialog(html) : null;
      }

      if (!freshDialog) continue;

      // Preserve updating state classes/live region by re-applying after swap
      const wasUpdating = currentDialog.classList.contains('is-updating');

      // Swap only the dialog’s inner HTML so this custom element instance stays intact
      currentDialog.innerHTML = freshDialog.innerHTML;

      // Restore state and keep the drawer open if it was open
      if (wasUpdating) currentDialog.classList.add('is-updating');
      currentDialog.setAttribute('aria-busy', wasUpdating ? 'true' : 'false');

      if (wasOpen && typeof currentDialog.showModal === 'function' && !currentDialog.open) {
        currentDialog.showModal();
      }

      return true; // success
    }

    return false;
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
